import { Injectable } from '@angular/core'
import { InfoDialogService, isSomething } from '@tokeet/tv3-platform'
import { Inquiry } from '@tv3/store/inquiry/inquiry.model'
import * as lodash from 'lodash'
import { AirbnbStandardFeeType } from '@tokeet-frontend/channels'
import { DecimalPipe } from '@angular/common'

@Injectable()
export class InquiryChannelBreakdownDialogsService {
  numberPipe = new DecimalPipe('en-US')
  constructor(private infoDialogService: InfoDialogService) {}

  toFixed(v: number) {
    return this.numberPipe.transform(v || 0, '1.2-2')
  }

  hasChannelPrice(inquiry: Inquiry) {
    return isSomething(inquiry.abbPrice) || isSomething(inquiry.expediaPrice) || isSomething(inquiry.bdcPrice)
  }

  onChannelBreakdown(inquiry: Inquiry, currency: string) {
    let breakdown = ''
    if (isSomething(inquiry.abbPrice)) {
      breakdown = this.onAirBnBBreakdown(inquiry, currency)
    } else if (isSomething(inquiry.expediaPrice)) {
      breakdown = this.onExpediaBreakdown(inquiry, currency)
    } else if (isSomething(inquiry.bdcPrice)) {
      breakdown = this.onBDCBreakdown(inquiry, currency)
    }

    if (!breakdown) {
      return
    }

    this.infoDialogService.open({
      title: 'Charge Detail',
      body: breakdown,
      closeText: 'Close',
    })
  }

  onAirBnBBreakdown(inquiry: Inquiry, currency: string) {
    if (!isSomething(inquiry.abbPrice)) {
      return
    }
    const abbPrice = inquiry.abbPrice

    const fees = lodash.map(abbPrice.standard_fees, (fee) => {
      if (fee.fee_type === AirbnbStandardFeeType.cleaningFee) return ''
      const name = lodash.startCase(lodash.toLower(fee.fee_type?.replace(/^pass_through/gi, '')))
      const amount = fee.amount_native
      if (!name || !amount) return ''
      return `
      <tr>
        <td>${name}:</td>
        <td align="right">+${currency}${this.toFixed(amount)}</td>
      </tr>
    `
    })
    const taxesTotal = lodash.sumBy(abbPrice.transient_occupancy_tax, (tax: any) => tax.amount_native || 0)
    const total = (abbPrice.payout || 0) + (abbPrice.fees || 0)

    return `
      <div class="row">
        <div class="offset-1 col-sm-10">
          <table border=0 class="w-100">
            <tr>
              <td width=120>Base charge:</td>
              <td align="right" width=120>${currency}${this.toFixed(abbPrice.base)}</td>
            </tr>
            <tr>
              <td>Cleaning Fee:</td>
              <td align="right">+${currency}${this.toFixed(abbPrice.extras)}</td>
            </tr>
            ${fees.join('')}
            <tr>
              <td>Taxes:</td>
              <td align="right">+${currency}${this.toFixed(taxesTotal)}</td>
            </tr>
            <tr>
              <td>Airbnb Fee:</td>
              <td align="right">-${currency}${this.toFixed(abbPrice.fees)}</td>
            </tr>
            <tr>
              <td colspan=2><hr noshade width=50% /></td>
            </tr>
            <tr>
              <td>Guest Paid:</td>
              <td align="right">${currency}${this.toFixed(total)}</td>
            </tr>
            <tr>
              <td>Host Payout:</td>
              <td align="right">${currency}${this.toFixed(abbPrice.payout)}</td>
            </tr>
          </table>
        </div>
      </div>
    `
  }

  onExpediaBreakdown(inquiry: Inquiry, currency: string) {
    if (!isSomething(inquiry.expediaPrice)) {
      return
    }

    const expediaPrice = inquiry.expediaPrice
    const paymentModel = expediaPrice.payment_model
    const isNet = /net/i.test(paymentModel)

    return `
      <div class="row">
        <div class="offset-1 col-sm-10">
          <table border=0 class="w-100">
           ${
             paymentModel
               ? `
           <tr>
              <td>Payment Model:</td>
              <td align="right">${paymentModel}</td>
            </tr>
           `
               : ''
           }
            <tr>
              <td width=120>${isNet ? 'User Charge:' : 'Base Charge:'}</td>
              <td align="right" width=120>${currency}${this.toFixed(
      isNet ? expediaPrice.total : expediaPrice.before_taxes
    )}</td>
            </tr>
            <tr>
              <td>Commission:</td>
              <td align="right">-${currency}${this.toFixed(expediaPrice.commission)}</td>
            </tr>
            <tr>
              <td>Taxes:</td>
              <td align="right">${isNet ? '-' : '+'}${currency}${this.toFixed(expediaPrice.taxes)}</td>
            </tr>
            <tr>
              <td colspan=2><hr noshade width=50% /></td>
            </tr>
            <tr>
              <td>Payout:</td>
              <td align="right">${currency}${this.toFixed(
      expediaPrice.payment_details?.amount || expediaPrice.total
    )}</td>
            </tr>
          </table>
        </div>
      </div>
    `
  }

  onBDCBreakdown(inquiry: Inquiry, currency: string) {
    if (!isSomething(inquiry.bdcPrice)) {
      return
    }
    const bdcPrice = inquiry.bdcPrice
    const total = bdcPrice.total || (bdcPrice.before_taxes || 0) + (bdcPrice.taxes || 0)

    return `
    <div class="row">
      <div class="offset-1 col-sm-10">
        <table border=0 class="w-100">
          <tr>
            <td width=120>Base Charge:</td>
            <td align="right" width=120>${currency}${this.toFixed(bdcPrice.before_taxes)}</td>
          </tr>
          <tr>
            <td>Commission:</td>
            <td align="right">-${currency}${this.toFixed(bdcPrice.commission)}</td>
          </tr>
          <tr>
            <td>Taxes:</td>
            <td align="right">+${currency}${this.toFixed(bdcPrice.taxes)}</td>
          </tr>
          <tr>
            <td colspan=2><hr noshade width=50% /></td>
          </tr>
          <tr>
            <td>Payout:</td>
            <td align="right">${currency}${this.toFixed(total)}</td>
          </tr>
        </table>
      </div>
    </div>
  `
  }
}
